import { useQueryClient } from '@tanstack/react-query';
import { NotebookPen } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  ACTIVITY_TYPE_LABELS,
  CALL_OUTCOMES,
  CALL_OUTCOME_LABELS,
  LOGGABLE_ACTIVITY_TYPES,
  NO_FOLLOW_UP_MESSAGE,
  type CallOutcome,
  type LoggableActivityType,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { TASKS_KEY, TIMELINE_KEY, communicationApi } from '../api/communication-api';
import styles from '../Communication.module.css';
import { useModule } from '../../../lib/modules';

const DAY_MS = 24 * 60 * 60 * 1000;
const DHAKA_OFFSET_MS = 6 * 60 * 60 * 1000;

/** Tomorrow in Dhaka, `YYYY-MM-DD` — a follow-up's sensible default (06-qol §7 smart defaults). */
function tomorrowInDhaka(): string {
  return new Date(Date.now() + DHAKA_OFFSET_MS + DAY_MS).toISOString().slice(0, 10);
}

/**
 * COMM-02 — "one-line quick logger on every candidate-facing screen: type, outcome, note,
 * next action + due date" (E13). It always asks "No follow-up?" when no next action is set,
 * because "a note without a next action is where candidates go to die" (master plan S26).
 */
export function QuickLogBar({ target }: { readonly target: { readonly applicationId: string | null; readonly candidateId: string | null } }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const [type, setType] = useState<LoggableActivityType>('call');
  const [outcome, setOutcome] = useState<CallOutcome | null>('no_answer');
  const [body, setBody] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [dueOn, setDueOn] = useState(tomorrowInDhaka);
  const [noFollowUp, setNoFollowUp] = useState(false);
  const [asked, setAsked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // R1 (§L32): with Tasks switched off a logged activity never becomes a follow-up, so nothing asks.
  const isTasksOn = useModule('tasks');

  const log = async (): Promise<void> => {
    const hasNext = isTasksOn && nextAction.trim().length >= 2;
    if (isTasksOn && !hasNext && !noFollowUp) {
      setAsked(true);
      return;
    }
    if (type === 'note' && body.trim() === '') {
      show({ tone: 'warning', message: 'Write the note first' });
      return;
    }
    setIsSaving(true);
    try {
      await communicationApi.log({
        applicationId: target.applicationId,
        candidateId: target.applicationId ? null : target.candidateId,
        type,
        outcome: type === 'call' ? outcome : null,
        body: body.trim(),
        nextAction: hasNext ? { title: nextAction.trim(), dueOn } : null,
        noFollowUp: !hasNext,
      });
      await Promise.all([queryClient.invalidateQueries({ queryKey: TIMELINE_KEY }), queryClient.invalidateQueries({ queryKey: TASKS_KEY })]);
      setBody('');
      setNextAction('');
      setNoFollowUp(false);
      setAsked(false);
      show({ tone: 'success', message: `${ACTIVITY_TYPE_LABELS[type]} logged`, detail: hasNext ? 'The follow-up is on your task list.' : undefined });
    } catch (cause) {
      show({ tone: 'danger', message: "It wasn't logged", detail: errorMessage(cause) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      className={styles.logBar}
      aria-label="Log an activity"
      onSubmit={(event) => {
        event.preventDefault();
        void log();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void log();
        }
      }}
    >
      <div className={styles.logRow}>
        <Segmented label="Activity type" value={type} options={LOGGABLE_ACTIVITY_TYPES.map((value) => ({ value, label: ACTIVITY_TYPE_LABELS[value] }))} onChange={setType} />
        <Input aria-label="What happened" value={body} maxLength={4000} placeholder={type === 'call' ? 'What was said' : type === 'note' ? 'Write the note' : 'What happened'} onChange={(event) => setBody(event.target.value)} />
      </div>
      <div className={styles.followRow}>
        {type === 'call' && (
          <Field label="Outcome">
            {({ inputId }) => <Select id={inputId} label="Outcome" value={outcome} options={CALL_OUTCOMES.map((value) => ({ value, label: CALL_OUTCOME_LABELS[value] }))} onChange={(value) => setOutcome(CALL_OUTCOMES.find((item) => item === value) ?? null)} />}
          </Field>
        )}
        {isTasksOn && (
          <Field label="Next action" isOptional>
            {({ inputId }) => (
              <Input
                id={inputId}
                value={nextAction}
                maxLength={200}
                placeholder="Call back about the interview time"
                onChange={(event) => {
                  setNextAction(event.target.value);
                  setAsked(false);
                }}
              />
            )}
          </Field>
        )}
        {isTasksOn && (
          <Field label="Due">
            {({ inputId }) => <DatePicker id={inputId} label="Due" value={dueOn} onChange={setDueOn} timeZone="Asia/Dhaka" />}
          </Field>
        )}
      </div>
      <div className={styles.logFooter}>
        {isTasksOn && (
          <Checkbox
            checked={noFollowUp}
            onChange={(checked) => {
              setNoFollowUp(checked);
              setAsked(false);
            }}
            label="No follow-up needed"
          />
        )}
        {asked && (
          <p className={styles.ask} role="alert">
            {NO_FOLLOW_UP_MESSAGE}
          </p>
        )}
        <Button variant="solid" type="submit" icon={NotebookPen} isPending={isSaving}>
          Log
        </Button>
      </div>
    </form>
  );
}
